<template>
  <div class="app">

    <!-- Phone-first column; centered with side gutters on wide screens -->
    <div class="app__page">
      <!-- HomeView is kept alive so the map, search and filters survive navigation -->
      <router-view v-slot="{ Component }">
        <KeepAlive :include="['HomeView']">
          <component :is="Component" />
        </KeepAlive>
      </router-view>
    </div>

    <div v-if="!hideFooter" class="fixed-bottom">
      <AppFooter />
    </div>

    <DataSharingConsentCard
      v-if="currentConsent"
      :consent="currentConsent"
      @responded="onConsentResponded"
      @dismissed="onConsentDismissed"
    />

    <!-- App-wide confirm dialog (driven by useConfirm) -->
    <ConfirmDialog />
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppFooter from "./components/global/AppFooter.vue";
import ConfirmDialog from "@/components/global/ConfirmDialog.vue";
import DataSharingConsentCard from "@/components/DataSharingConsentCard.vue";
import { useTheme } from "@/composables/useTheme";
import { usePolicyStatus } from "@/composables/usePolicyStatus";
import { me, profileCompletion } from "@/services/auth";

useTheme();

const route  = useRoute();
const router = useRouter();

// Hide footer on routes that set meta.hideFooter = true
const hideFooter = computed(() => route.meta?.hideFooter === true);

const { pendingConsents, checkStatus, clearConsent } = usePolicyStatus();

const currentConsent = computed(() =>
  pendingConsents.value.length ? pendingConsents.value[0] : null
);

function onConsentResponded({ consentId }) { clearConsent(consentId); }
function onConsentDismissed(consentId)    { clearConsent(consentId); }

const ALWAYS_ALLOW = ["PolicyAcceptance", "StaticPage", "StaticPageIndex", "Account", "ProfileEdit", "QrLanding"];

// Refresh the user (and its profile_completion) on load, then gate if incomplete.
async function refreshProfileGate() {
  if (!localStorage.getItem("token")) return;
  try { await me(); } catch { /* 401 handled by api interceptor */ }
  const completion = profileCompletion();
  if (completion?.complete === false && !ALWAYS_ALLOW.includes(route.name)) {
    router.replace({ name: "ProfileEdit", query: { gate: "1", redirect: route.fullPath } });
  }
}

onMounted(() => {
  checkStatus();
  refreshProfileGate();
});

window.addEventListener("storage", (e) => {
  if (e.key === "token" && e.newValue) { checkStatus(); refreshProfileGate(); }
});
</script>

<style lang="scss">
.app {
  width: 100%;
  min-height: 100vh;
  display: block;
  background-color: var(--color-deep);

  &__page {
    width: 100%;
    max-width: var(--app-max-width);
    min-height: 100vh;
    margin: 0 auto;
    background-color: var(--color-bg);
  }
}

// Wide screens: hairline edges so the column reads as a deliberate frame
@media (min-width: 720px) {
  .app__page { box-shadow: -1px 0 0 var(--color-border-soft), 1px 0 0 var(--color-border-soft); }
}

// Bootstrap's .fixed-bottom (AppFooter) stays inside the column
.fixed-bottom {
  left: var(--app-gutter);
  right: var(--app-gutter);
}
</style>